import { getCountries, getCountryCallingCode } from 'libphonenumber-js/core';
import metadata from 'libphonenumber-js/metadata.full.json';
import { useEffect, useState } from 'react';

import type { PhoneNumber } from '@/types/crm';

type CountryCode = string;
import { useOrg } from '@/contexts/OrgContext';
import { InputBase } from '@/lib/base/forms/input/input';
import { InputGroup } from '@/lib/base/forms/input/input-group';
import { NativeSelect } from '@/lib/base/forms/select/select-native';
import type { SelectSize } from '@/lib/base/forms/select/select-shared';
import { sizes } from '@/lib/base/forms/select/select-shared';
import { getCountryFlagEmoji } from '@/lib/crm/utils/phone';
import { cx } from '@/utils/cx';

import type { PropertyInputProps } from './TextInput';

// Get all countries from libphonenumber-js
const countries = getCountries(metadata as any); // eslint-disable-line @typescript-eslint/no-explicit-any -- libphonenumber-js type mismatch with our data model

// Create country options with country code and flag. The flag comes from the
// shared helper the read-only PhoneValueLabel uses, so the picker and the cell
// can't drift apart.
const countryOptions = countries
  .map(country => ({
    value: country,
    label: [country, getCountryFlagEmoji(country)].filter(Boolean).join(' '), // Country code + flag in dropdown
  }))
  .sort((a, b) => a.value.localeCompare(b.value)); // Sort by country code

/**
 * Base PhoneInput component props
 */
export interface PhoneInputBaseProps {
  /** Current phone number value in structured format */
  value?: PhoneNumber;
  /** Callback when phone number changes */
  onChange: (value: PhoneNumber | undefined) => void;
  /** Helper text displayed below the input */
  hint?: string;
  /** Whether the input is disabled */
  isDisabled?: boolean;
  /** Placeholder text */
  placeholder?: string;
  /** Default country */
  defaultCountry?: CountryCode;
  /** Name attribute for form submission */
  name?: string;
  /**
   * Field size on the shared library scale (`SelectSize`). Drives both the
   * number input and the country selector. Defaults to 'sm'.
   */
  size?: SelectSize;
  /** Accessibility label */
  ariaLabel?: string;
  /** Trailing addon element (e.g., enrich button) */
  trailingAddon?: React.ReactNode;
  /**
   * Host context, mirroring MarkdownInput's prop of the same name.
   *
   * 'form' (default) fills the column, so the field lines up with every other
   * field beside it. 'card' caps the width, which the table cell's popped
   * field depends on: `cell-field-overlay` passes `cardMinWidthPx={0}` for
   * phone and sizes the overlay to the field's own natural width.
   */
  display?: 'form' | 'card';
  /** Optional ref to forward to the underlying input element */
  ref?: React.RefObject<HTMLInputElement | null>;
}

/**
 * PhoneInputBase - Standalone phone input component with country selector.
 * Used for phone number input with structured format.
 */
export const PhoneInputBase = ({
  value,
  onChange,
  hint,
  isDisabled = false,
  placeholder = '(555) 000-0000',
  defaultCountry = 'US',
  name,
  size = 'sm',
  ariaLabel,
  trailingAddon,
  display = 'form',
  ref,
}: PhoneInputBaseProps) => {
  // Parse current value to extract country and national number
  const parseValue = (
    val?: PhoneNumber
  ): { country: CountryCode; number: string } => {
    if (!val) {
      return { country: defaultCountry, number: '' };
    }

    // Extract country and national number from structured format
    // Fallback to defaultCountry if country_code is missing or invalid
    const countryCode = val.country_code;
    const isValidCountry =
      countryCode && (countries as readonly string[]).includes(countryCode);

    return {
      country: isValidCountry ? countryCode : defaultCountry,
      number: val.national_number || '',
    };
  };

  const parsed = parseValue(value);
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(
    parsed.country
  );
  const [nationalNumber, setNationalNumber] = useState(parsed.number);

  // Update local state when value or defaultCountry prop changes
  useEffect(() => {
    const parsed = parseValue(value);
    setSelectedCountry(parsed.country);
    setNationalNumber(parsed.number);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- parseValue defined inside component but depends on defaultCountry which is already in deps; adding parseValue would cause infinite loop
  }, [value, defaultCountry]);

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newCountry = e.target.value as CountryCode;
    setSelectedCountry(newCountry);

    // Update value with new country code
    if (nationalNumber) {
      onChange({
        country_code: newCountry,
        national_number: nationalNumber,
      });
    }
  };

  const handleNumberChange = (val: string) => {
    // Remove all non-digits
    const cleaned = val.replace(/\D/g, '');
    setNationalNumber(cleaned);

    if (cleaned) {
      onChange({
        country_code: selectedCountry,
        national_number: cleaned,
      });
    } else {
      onChange(undefined); // Empty input clears the property
    }
  };

  const callingCode = getCountryCallingCode(
    selectedCountry as any, // eslint-disable-line @typescript-eslint/no-explicit-any -- libphonenumber-js type mismatch with our data model
    metadata as any // eslint-disable-line @typescript-eslint/no-explicit-any -- libphonenumber-js type mismatch with our data model
  );

  return (
    // In a form the field fills its column like every other field — a width
    // cap here made it visibly narrower than its neighbours AND squeezed the
    // number input until the value clipped.
    //
    // In 'card' (the table cell's popped field) the cap stays: that overlay
    // has no column to fill, so it sizes itself to the field's natural width
    // and passes `cardMinWidthPx={0}` on the strength of this cap. 17rem fits
    // the country select + calling code + a full-length national number.
    <div className={cx('w-full', display === 'card' && 'max-w-[17rem]')}>
      <InputGroup
        aria-label={ariaLabel || 'Phone number'}
        hint={hint}
        size={size}
        value={nationalNumber}
        onChange={handleNumberChange}
        isDisabled={isDisabled}
        name={name}
        leadingAddon={
          <div className='flex items-center gap-2'>
            <NativeSelect
              aria-label='Country'
              options={countryOptions}
              value={selectedCountry}
              onChange={handleCountryChange}
              disabled={isDisabled}
              // The picker only shows a 2-letter code + flag. Its default width came
              // almost entirely from an oversized right padding reserved for the
              // chevron (~40px at the md size). Follow the field's own size (default
              // `sm`, a smaller chevron) and trim that padding so the picker sizes to
              // its content — compact, but still showing the flag and chevron without
              // clipping. No fixed width, so nothing is cut off.
              //
              // pr-4 == the flush-right chevron's own width (16px), so the flag sits
              // right up against the chevron with no dead space between them.
              //
              // Height is NOT pinned here. NativeSelect now carries an unbroken
              // h-full chain inside an InputGroup, so the select matches the
              // group at every size and viewport. A fixed `!h-10` would freeze
              // it at the desktop `sm` height and break the mobile step.
              size={size}
              selectClassName='!pr-4'
            />
            {/* Dial code takes its font from the shared scale rather than a
                hardcoded `text-md`, so it shrinks with the field on mobile
                instead of forcing the leading addon taller than the input. */}
            <span
              className={cx(
                'text-tertiary whitespace-nowrap',
                sizes[size].text
              )}
            >
              +{callingCode}
            </span>
          </div>
        }
        trailingAddon={trailingAddon}
      >
        <InputBase type='tel' placeholder={placeholder} size={size} ref={ref} />
      </InputGroup>
    </div>
  );
};

export interface PhoneInputAdapterProps extends PropertyInputProps {
  /** Trailing addon element (e.g., enrich button) */
  trailingAddon?: React.ReactNode;
  /** Host context — see `display` on PhoneInputBaseProps. */
  display?: 'form' | 'card';
}

/**
 * PhoneInput - PropertyInput adapter for phone number values.
 * Wraps PhoneInputBase with PropertyInputProps interface.
 */
export const PhoneInput: React.FC<PhoneInputAdapterProps> = ({
  propertyType,
  value,
  onChange,
  disabled = false,
  trailingAddon,
  inputRef,
  placeholder,
  display = 'form',
  size = 'sm',
}) => {
  const handleChange = (newValue: PhoneNumber | undefined) => {
    onChange(newValue);
  };

  const { currentOrg } = useOrg();
  const region = currentOrg?.defaultPhoneRegion;
  const defaultCountry =
    region && (countries as readonly string[]).includes(region) ? region : 'US';

  return (
    <PhoneInputBase
      value={value as PhoneNumber | undefined}
      onChange={handleChange}
      isDisabled={disabled}
      name={propertyType.property_type_name}
      size={size}
      ariaLabel={propertyType.label}
      trailingAddon={trailingAddon}
      placeholder={placeholder}
      display={display}
      defaultCountry={defaultCountry}
      ref={inputRef}
    />
  );
};
